/**
 * HR Hank Dashboard
 * Main dashboard for HR persona - manages post-offer onboarding and coordination
 * 
 * Refactored for better maintainability:
 * - Extracted data fetching to useHRData hook
 * - Moved constants to hrConstants.js
 * - Created service layer for API calls
 * - Split tabs into separate components
 */

import React, { useState, useMemo } from 'react';
import { Briefcase, Filter, RefreshCw, Clock, Activity, UserCheck, BarChart3 } from 'lucide-react';
import HROverviewTab from '../components/hr/HROverviewTab';
import HRCandidatesTab from '../components/hr/HRCandidatesTab';
import HRReportsTab from '../components/hr/HRReportsTab';
import { useHRData } from '../hooks/useHRData';
import { ONBOARDING_STATUSES, AGING_BUCKETS } from '../constants/hrConstants';

const HRHankDashboard = () => {
  // UI State
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [includeOlderJobs, setIncludeOlderJobs] = useState(false);

  // Fetch data using custom hook
  const { loading, submissions, candidates, jobs, clients, recruiters, refetch } = useHRData(includeOlderJobs);

  /**
   * Calculate accepted candidates with aging and onboarding status
   */
  const acceptedCandidates = useMemo(() => {
    const acceptedOffers = submissions.filter(s => s.current_status === 'offer_accepted');
    const today = new Date();

    return acceptedOffers.map(sub => {
      const candidate = candidates.find(c => c.id === sub.candidate_id);
      const job = jobs.find(j => j.id === sub.job_id);
      
      const offerAcceptedDate = sub.updated_at ? new Date(sub.updated_at) : new Date(sub.submission_date);
      const daysSinceAccepted = Math.floor((today - offerAcceptedDate) / (1000 * 60 * 60 * 24));
      
      // Determine aging bucket
      const agingBucket = AGING_BUCKETS.find(b => 
        daysSinceAccepted >= b.min && daysSinceAccepted <= b.max
      ) || AGING_BUCKETS[AGING_BUCKETS.length - 1];

      // Mock onboarding status (would come from actual onboarding system)
      const onboardingStatus = daysSinceAccepted > 30 ? ONBOARDING_STATUSES.DELAYED :
                               daysSinceAccepted > 14 ? ONBOARDING_STATUSES.AT_RISK :
                               daysSinceAccepted > 7 ? ONBOARDING_STATUSES.IN_PROGRESS :
                               ONBOARDING_STATUSES.PENDING;

      return {
        ...sub,
        candidate,
        job,
        daysSinceAccepted,
        agingBucket,
        onboardingStatus,
        offerAcceptedDate
      };
    });
  }, [submissions, candidates, jobs]);

  /**
   * Tab configuration
   */
  const tabs = [
    { key: 'overview', label: 'Overview', icon: Activity, component: HROverviewTab },
    { key: 'candidates', label: 'Candidates Onboarding', icon: UserCheck, component: HRCandidatesTab },
    { key: 'reports', label: 'Reports & Analytics', icon: BarChart3, component: HRReportsTab }
  ];

  /**
   * Render active tab content
   */
  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12" data-testid="loading-spinner">
          <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
        </div>
      );
    }

    switch (activeTab) {
      case 'overview':
        return (
          <HROverviewTab
            submissions={submissions}
            jobs={jobs}
            candidates={candidates}
            users={recruiters}
            clients={clients}
          />
        );
      
      case 'candidates':
        return (
          <HRCandidatesTab
            acceptedCandidates={acceptedCandidates}
            clients={clients}
            recruiters={recruiters}
            onCandidateSelect={setSelectedCandidate}
          />
        );
      
      case 'reports':
        return <HRReportsTab />;
      
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="hr-hank-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="h-12 w-12 bg-blue-600 rounded-full flex items-center justify-center text-white">
              <Briefcase className="h-6 w-6" />
            </div>
            HR Hank Dashboard
          </h1>
          <p className="text-gray-600 mt-1">Manage post-offer onboarding and coordination</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={includeOlderJobs}
              onChange={(e) => setIncludeOlderJobs(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
              data-testid="include-older-jobs-toggle"
            />
            <Clock className="h-4 w-4" />
            <span className="text-sm">Include older jobs</span>
          </label>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            data-testid="filters-toggle-btn"
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
          <button
            onClick={refetch}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px" data-testid="tabs-nav">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.key
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  data-testid={`tab-${tab.key}`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default HRHankDashboard;
